/**
 * Sleep Doctor Homepage — Scroll Animation Engine
 * Uses IntersectionObserver for performant scroll-triggered animations.
 * No dependencies. Vanilla JS.
 */
(function() {
  'use strict';

  /* ── CONFIG ─────────────────────────────────────────────── */
  const DEFAULTS = {
    threshold: 0.15,
    rootMargin: '0px 0px -60px 0px',
    staggerDelay: 120,
    once: true
  };

  /* ── OBSERVER: reveal elements with [data-sd-animate] ──── */
  function initRevealObserver() {
    const targets = document.querySelectorAll('[data-sd-animate]');
    if (!targets.length) return;

    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        const el = entry.target;

        // Stagger: if inside a [data-sd-stagger] parent, delay by index
        const parent = el.closest('[data-sd-stagger]');
        if (parent) {
          const siblings = parent.querySelectorAll('[data-sd-animate]');
          const idx = Array.prototype.indexOf.call(siblings, el);
          const delay = idx * (parseInt(parent.dataset.sdStagger) || DEFAULTS.staggerDelay);
          el.style.transitionDelay = delay + 'ms';
        }

        // Custom delay override
        if (el.dataset.sdDelay) {
          el.style.transitionDelay = el.dataset.sdDelay + 'ms';
        }

        el.classList.add('sd-visible');
        if (DEFAULTS.once) observer.unobserve(el);
      });
    }, {
      threshold: DEFAULTS.threshold,
      rootMargin: DEFAULTS.rootMargin
    });

    targets.forEach(function(t) { observer.observe(t); });
  }

  /* ── PARALLAX: subtle background shift on scroll ───────── */
  function initParallax() {
    var parallaxEls = document.querySelectorAll('[data-sd-parallax]');
    if (!parallaxEls.length) return;

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function() {
        var scrollY = window.pageYOffset;
        parallaxEls.forEach(function(el) {
          var speed = parseFloat(el.dataset.sdParallax) || 0.3;
          var rect = el.getBoundingClientRect();
          var elTop = rect.top + scrollY;
          var offset = (scrollY - elTop) * speed;
          el.style.transform = 'translate3d(0,' + offset + 'px,0)';
        });
        ticking = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ── COUNTER: animate numbers on scroll ────────────────── */
  function initCounters() {
    var counters = document.querySelectorAll('[data-sd-counter]');
    if (!counters.length) return;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var target = parseInt(el.dataset.sdCounter);
        var suffix = el.dataset.sdSuffix || '';
        var prefix = el.dataset.sdPrefix || '';
        var duration = parseInt(el.dataset.sdDuration) || 2000;
        var start = 0;
        var startTime = null;

        function easeOutExpo(t) {
          return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
        }

        function step(timestamp) {
          if (!startTime) startTime = timestamp;
          var progress = Math.min((timestamp - startTime) / duration, 1);
          var value = Math.floor(easeOutExpo(progress) * target);
          el.textContent = prefix + value.toLocaleString() + suffix;
          if (progress < 1) {
            requestAnimationFrame(step);
          } else {
            el.textContent = prefix + target.toLocaleString() + suffix;
          }
        }
        requestAnimationFrame(step);
        observer.unobserve(el);
      });
    }, { threshold: 0.5 });

    counters.forEach(function(c) { observer.observe(c); });
  }

  /* ── NAVBAR: shrink on scroll ──────────────────────────── */
  function initNavbarShrink() {
    var header = document.querySelector('.site-header');
    if (!header) return;
    var shrinkClass = 'sd-header-scrolled';
    var lastScroll = 0;

    window.addEventListener('scroll', function() {
      var scrollY = window.pageYOffset;
      if (scrollY > 80) {
        header.classList.add(shrinkClass);
      } else {
        header.classList.remove(shrinkClass);
      }
      lastScroll = scrollY;
    }, { passive: true });
  }

  /* ── PROGRESS BAR (optional) ───────────────────────────── */
  function initProgressBar() {
    var bar = document.getElementById('sd-progress-bar');
    if (!bar) return;

    window.addEventListener('scroll', function() {
      var scrollTop = window.pageYOffset;
      var docHeight = document.documentElement.scrollHeight - window.innerHeight;
      var progress = (scrollTop / docHeight) * 100;
      bar.style.width = progress + '%';
    }, { passive: true });
  }

  /* ── SMOOTH SCROLL for anchor links ────────────────────── */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#sd-"]').forEach(function(link) {
      link.addEventListener('click', function(e) {
        var target = document.querySelector(this.getAttribute('href'));
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  /* ── INIT ───────────────────────────────────────────────── */
  /* ── HERO: force-reveal elements already in viewport on load ── */
  function initHeroReveal() {
    var hero = document.getElementById('sd-hero');
    if (!hero) return;
    var heroEls = hero.querySelectorAll('[data-sd-animate]');
    heroEls.forEach(function(el, i) {
      var delay = parseInt(el.dataset.sdDelay) || (i * 120);
      setTimeout(function() { el.classList.add('sd-visible'); }, delay);
    });
  }

  function init() {
    initHeroReveal();
    initRevealObserver();
    initParallax();
    initCounters();
    initNavbarShrink();
    initProgressBar();
    initSmoothScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
